import { CalendarMonth, ChevronRight, Circle, FilterAltOff, Groups, Person } from "@mui/icons-material";
import { Button, MenuItem, TablePagination, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { companyLabel, situationColors, situationLabels } from "../../components/epi/epiColors";
import { EpiInspectionFlowService } from "../../services/EpiInspectionFlowService";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiEmployeeSelectionDTO } from "../../types/EpiEmployeeSelectionDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiInspectionNew.scss';

export { situationLabels, situationColors };

const pageSizes = [25, 50, 100];

const EpiInspectionNew: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [employees, setEmployees] = useState<EpiEmployeeSelectionDTO[]>([]);
	const [sector, setSector] = useState('');
	const [position, setPosition] = useState('');
	const [search, setSearch] = useState('');
	const [situation, setSituation] = useState('');
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { showError } = useAlert();
	const { verifyPermission } = useUtils();
	const service = new EpiInspectionFlowService();
	const configService = new EpiInspectionService();

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await configService.getCompanies();
			const enabled = (response?.data || []).filter(company => company.enabled);
			setCompanies(enabled);
			if (enabled.length === 1) {
				setCompanyId(enabled[0].companyId);
			}
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'CREATE_EPI_INSPECTION');
		}
	}, []);

	useEffect(() => {
		const loadEmployees = async () => {
			if (companyId === '') {
				setEmployees([]);
				return;
			}
			setLoading(true);
			const response = await service.listEmployees(Number(companyId));
			setEmployees(response?.data || []);
			setPage(0);
			setLoading(false);
		};
		loadEmployees();
	}, [companyId]);

	const sectors = distinct(employees.map(employee => employee.sector));
	const positions = distinct(employees.map(employee => employee.position));
	const filtered = filter();
	const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
	const visible = filtered.slice(currentPage * size, currentPage * size + size);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-new">
				<div className="topo">
					<Typography variant="h6">Inspeção de EPIs / Selecionar colaborador</Typography>
					<span>Selecione a empresa, função e o colaborador para iniciar uma nova inspeção de EPI.</span>
				</div>
				<div className="filtros">
					<TextField
						select
						label="Empresa"
						size="small"
						value={companyId}
						onChange={(e) => setCompanyId(Number(e.target.value))}
					>
						{companies.map(company => (
							<MenuItem key={company.companyId} value={company.companyId}>
								{companyLabel(company)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Setor"
						size="small"
						value={sector}
						onChange={(e) => { setSector(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos</MenuItem>
						{sectors.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Cargo"
						size="small"
						value={position}
						onChange={(e) => { setPosition(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos</MenuItem>
						{positions.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<TextField
						label="Buscar por nome ou matrícula"
						size="small"
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(0); }}
					/>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Situação"
						size="small"
						value={situation}
						onChange={(e) => { setSituation(e.target.value); setPage(0); }}
					>
						<MenuItem value="">Todos</MenuItem>
						{Object.keys(situationLabels).map(key => (
							<MenuItem key={key} value={key}>{situationLabels[key]}</MenuItem>
						))}
					</TextField>
					<div className="botoes">
						<Button variant="outlined" startIcon={<FilterAltOff />} onClick={clearFilters}>
							LIMPAR FILTROS
						</Button>
					</div>
				</div>
				{!loading && companyId !== '' && (
					<div className="resultados">
						<span className="contagem">
							<Groups fontSize="small" />
							{filtered.length === 1
								? '1 colaborador encontrado'
								: `${filtered.length} colaboradores encontrados`}
						</span>
						<div className="legenda">
							{Object.keys(situationLabels).map(key => (
								<span key={key}>
									<i style={{ backgroundColor: situationColors[key] }} />
									{situationLabels[key]}
								</span>
							))}
						</div>
					</div>
				)}
				{loading
					? getLoading()
					: !filtered.length
						? <div className="sem-execucoes">
							<Typography variant="h6">
								{companyId === ''
									? 'Selecione uma empresa para listar os colaboradores.'
									: 'Nenhum colaborador encontrado.'}
							</Typography>
						</div>
						: <Fragment>
							<div className="cards">
								{visible.map(employee => (
									<div className="card-colaborador" key={employee.employeeId}>
										<div className="cabecalho">
											<span
												className="avatar"
												style={{ backgroundColor: situationColors[employee.situation] }}
											>
												<Person />
											</span>
											<div className="identidade">
												<Typography variant="subtitle1">
													{(employee.employeeName || '').toUpperCase()}
												</Typography>
												<p><strong>Matrícula: </strong>{employee.registration || '—'}</p>
												<p><strong>Função: </strong>{employee.position || '—'}</p>
												<p><strong>Setor: </strong>{employee.sector || '—'}</p>
											</div>
										</div>
										<div className="divisoria" />
										<span
											className="situacao"
											style={{ color: situationColors[employee.situation] }}
										>
											<Circle fontSize="small" />
											{situationLabels[employee.situation] || employee.situation}
										</span>
										<span className="secundaria">{secondaryLine(employee)}</span>
										<span className="ultima">
											<CalendarMonth fontSize="small" />
											Última inspeção: {employee.lastInspectionDate || '—'}
										</span>
										{employee.situation !== 'NAO_APLICAVEL' && (
											<Button
												variant="contained"
												fullWidth
												endIcon={<ChevronRight />}
												style={{ backgroundColor: situationColors[employee.situation] }}
												onClick={() => startInspection(employee.employeeId)}
											>
												REALIZAR INSPEÇÃO
											</Button>
										)}
									</div>
								))}
							</div>
							<div className="paginacao">
								<TablePagination
									component="div"
									rowsPerPageOptions={pageSizes}
									count={filtered.length}
									rowsPerPage={size}
									page={currentPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
									labelRowsPerPage="Registros por página"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	async function startInspection(employeeId: number) {
		try {
			const response = await service.createOrResume(employeeId);
			navigate(`/inspecao-epis/nova/${response.data.id}`);
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível iniciar a inspeção.');
		}
	}

	function filter() {
		return employees.filter(employee => {
			if (sector && employee.sector !== sector) {
				return false;
			}
			if (position && employee.position !== position) {
				return false;
			}
			if (situation && employee.situation !== situation) {
				return false;
			}
			if (!search) {
				return true;
			}
			return (employee.employeeName || '').toUpperCase().includes(search.toUpperCase())
				|| (employee.registration || '').includes(search);
		});
	}

	function clearFilters() {
		setSector('');
		setPosition('');
		setSearch('');
		setSituation('');
		setPage(0);
	}

	function secondaryLine(employee: EpiEmployeeSelectionDTO) {
		if (employee.situation === 'NAO_APLICAVEL') {
			return 'Função administrativa';
		}
		if (employee.situation === 'NAO_INSPECIONADO') {
			return 'Realizar inspeção';
		}
		if (employee.situation === 'VENCENDO') {
			return employee.daysToDue === undefined || employee.daysToDue === null
				? 'Inspeção próxima do vencimento'
				: `Vence em ${employee.daysToDue} ${employee.daysToDue === 1 ? 'dia' : 'dias'}`;
		}
		const total = employee.inspectionsInPeriod || 0;
		return total === 1 ? '1 inspeção' : `${total} inspeções`;
	}

	function distinct(values: string[]) {
		return Array.from(new Set(values.filter(value => !!value))).sort();
	}
};

export default EpiInspectionNew;
